/**
 * Tenant Settings Panel — configuration for currency, timezone, date format, country,
 * plus branding (company name, primary/accent colours, logo upload).
 * Branding colours default to the canonical Rosta palette (Brand.md):
 * primary #084386 (steel-azure), accent #64A6F7 (cool-horizon).
 * @see Documentation/Specification/04_Processes/application-agnostic/tenant/TenantSettings.md
 * @see Documentation/Organisation/07_Sales_and_Marketing/Corporate_Identity/Brand.md
 */
import { useState, useEffect, useRef } from 'react';
import { useGetOne, useUpdate, useNotify, useNavigate } from 'react-admin';
import {
  Box, Typography, Paper, Button, Grid, CircularProgress, Divider, Alert,
} from '@mui/material';
import { Save, Upload } from '@mui/icons-material';
import { fetchApi } from '../../api/fetchApi';

const CURRENCIES = ['ZAR', 'USD', 'GBP', 'EUR', 'AUD', 'CAD', 'SGD'];
const TIMEZONES = [
  'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Cairo',
  'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Chicago',
  'America/Los_Angeles', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
];
const COUNTRIES = [
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'EG', name: 'Egypt' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'AU', name: 'Australia' },
];

/** Canonical Rosta defaults — Brand.md (steel-azure / cool-horizon). */
const DEFAULT_PRIMARY = '#084386';
const DEFAULT_ACCENT = '#64A6F7';

const inputSx = { width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: 14 };

interface Props {
  tenantId: string;
}

export const TenantSettingsPanel = ({ tenantId }: Props) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [update] = useUpdate();
  const { data: tenant, isLoading } = useGetOne('tenants', { id: tenantId });
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState('ZAR');
  const [timezone, setTimezone] = useState('Africa/Johannesburg');
  const [dateFormat, setDateFormat] = useState('dd/MM/yyyy');
  const [country, setCountry] = useState('ZA');
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && tenant) {
      const t = tenant as any;
      setCurrency(t.settings?.currency ?? 'ZAR');
      setTimezone(t.settings?.timezone ?? 'Africa/Johannesburg');
      setDateFormat(t.settings?.dateFormat ?? 'dd/MM/yyyy');
      setCountry(t.settings?.country ?? 'ZA');
      setCompanyName(t.settings?.branding?.companyName ?? '');
      setPrimaryColor(t.settings?.branding?.primaryColor ?? DEFAULT_PRIMARY);
      setAccentColor(t.settings?.branding?.accentColor ?? DEFAULT_ACCENT);
      setLogoDataUrl(t.settings?.branding?.logo ?? null);
      setInitialized(true);
    }
  }, [tenant, initialized]);

  if (isLoading || !tenant) {
    return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await update('tenants', {
        id: tenantId,
        meta: { method: 'PATCH', path: `${tenantId}/settings` },
        data: {
          currency, timezone, dateFormat, country,
          branding: { companyName: companyName || undefined, primaryColor, accentColor },
        },
      });
      notify('Settings updated', { type: 'success' });
    } catch (err: any) {
      notify(err?.message ?? 'Failed to update settings', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 200 * 1024) {
      notify('Logo must be 200KB or smaller', { type: 'warning' });
      return;
    }
    setUploadingLogo(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const res = await fetchApi(`/tenants/${tenantId}/logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logo: dataUrl }),
      });
      if (!res.ok) throw new Error('Logo upload failed');
      setLogoDataUrl(dataUrl);
      notify('Logo uploaded', { type: 'success' });
    } catch (err: any) {
      notify(err?.message ?? 'Logo upload failed', { type: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Tenant Settings</Typography>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving} size="small">
          {saving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Currency</Typography>
            <Box component="select" value={currency} onChange={(e: any) => setCurrency(e.target.value)}
              sx={{ ...inputSx, appearance: 'auto' }}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Country</Typography>
            <Box component="select" value={country} onChange={(e: any) => setCountry(e.target.value)}
              sx={{ ...inputSx, appearance: 'auto' }}>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Timezone</Typography>
            <Box component="select" value={timezone} onChange={(e: any) => setTimezone(e.target.value)}
              sx={{ ...inputSx, appearance: 'auto' }}>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Date Format</Typography>
            <Box component="select" value={dateFormat} onChange={(e: any) => setDateFormat(e.target.value)}
              sx={{ ...inputSx, appearance: 'auto' }}>
              {['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'dd.MM.yyyy'].map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Branding</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Colours default to the Rosta palette (steel-azure #084386, cool-horizon #64A6F7). Override per tenant as needed.
        </Alert>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Company Name</Typography>
            <Box component="input" value={companyName} onChange={(e: any) => setCompanyName(e.target.value)}
              placeholder="Displayed company name" sx={inputSx} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Primary Colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="input" type="color" value={primaryColor}
                onChange={(e: any) => setPrimaryColor(e.target.value)}
                sx={{ width: 48, height: 36, p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
              <Typography variant="body2" fontFamily="monospace">{primaryColor}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Accent Colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="input" type="color" value={accentColor}
                onChange={(e: any) => setAccentColor(e.target.value)}
                sx={{ width: 48, height: 36, p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
              <Typography variant="body2" fontFamily="monospace">{accentColor}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Typography variant="caption" color="text.secondary">Logo (max 200KB)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
              {logoDataUrl ? (
                <Box component="img" src={logoDataUrl} alt="Tenant logo"
                  sx={{ height: 48, maxWidth: 160, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }} />
              ) : (
                <Typography variant="body2" color="text.secondary">No logo uploaded</Typography>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                style={{ display: 'none' }}
                onChange={(e) => handleLogoSelect(e.target.files?.[0])}
              />
              <Button size="small" variant="outlined" startIcon={<Upload />} disabled={uploadingLogo}
                onClick={() => fileInputRef.current?.click()}>
                {uploadingLogo ? <CircularProgress size={16} /> : logoDataUrl ? 'Replace' : 'Upload'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
